import type { Vector2 } from '@sorrem/shared';
import { SKILL_TREE_INTRO_SPEED_MOD, SKILL_TREE_OUTRO_SPEED_MOD, type SkillTreeIntroDirection, type SkillTreeIntroWindow } from './skill-tree-intro';
import type { SkillTreeIntroEffect } from './skill-tree-node-intros';
import { clamp01, lerp } from './skill-tree-math';
import { MAX_FRAME_SECONDS } from './skill-tree-canvas';

/**
 * How a part of the tree moves through its window of the intro: a node
 * part's entrance (`SkillTreeIntroEffect`), `sweep` for a mask stroke that
 * draws a link or fills a gauge, or `pulse` for the sigil's swell.
 */
export type SkillTreeIntroMotion = SkillTreeIntroEffect | 'sweep' | 'pulse';

export interface SkillTreeIntroPartOptions {
    /** The point the part scales about, in its own coordinates. */
    readonly origin?: Vector2;
    /** Where a `grow-out` starts, relative to the part's place. */
    readonly from?: Vector2;
}

/** The look of one part at a moment of its motion: what the clock writes onto its element. */
export interface SkillTreeIntroPose {
    readonly opacity?: number;
    readonly transform?: string;
    /** For `sweep`: 100 hides the stroke, 0 draws it fully (the stroke has `pathLength` 100). */
    readonly strokeDashoffset?: number;
}

function easeOut(value: number): number {
    return 1 - (1 - value) ** 3;
}

/**
 * The part's look at `progress` through its window (0 before it starts, 1
 * once it has arrived). The same function serves both directions: the
 * reverse is the intro's clock running backward.
 */
export function resolveSkillTreeIntroPose(motion: SkillTreeIntroMotion, progress: number, options: SkillTreeIntroPartOptions = {}): SkillTreeIntroPose {
    const p = clamp01(progress);
    const eased = easeOut(p);
    switch (motion) {
        case 'pop': {
            // Up past full size, then back: an overshoot at 70%.
            const scale = p < 0.7 ? lerp(0.3, 1.08, easeOut(p / 0.7)) : lerp(1.08, 1, (p - 0.7) / 0.3);
            return { opacity: clamp01(p / 0.5), transform: `scale(${scale})` };
        }
        case 'materialize':
            return { opacity: eased, transform: `scale(${lerp(1.35, 1, eased)})` };
        case 'settle':
            return { opacity: eased, transform: `scale(${lerp(1.25, 1, eased)})` };
        case 'grow-out': {
            const from = options.from ?? { x: 0, y: 0 };
            const left = 1 - eased;
            return { opacity: clamp01(p / 0.3), transform: `translate(${from.x * left}px, ${from.y * left}px) scale(${lerp(0.2, 1, eased)})` };
        }
        case 'fade':
            return { opacity: eased };
        case 'sweep':
            return { strokeDashoffset: 100 * (1 - eased) };
        case 'pulse':
            return { transform: `scale(${1 + 0.08 * Math.sin(Math.PI * p)})` };
    }
}

interface Part {
    readonly window: SkillTreeIntroWindow;
    readonly motion: SkillTreeIntroMotion;
    readonly options: SkillTreeIntroPartOptions;
}

function writePose(element: SVGElement, pose: SkillTreeIntroPose): void {
    const { style } = element;
    style.opacity = pose.opacity === undefined ? '' : String(pose.opacity);
    style.transform = pose.transform ?? '';
    style.strokeDashoffset = pose.strokeDashoffset === undefined ? '' : String(pose.strokeDashoffset);
}

/**
 * The Skills tab intro's one clock. Its position is a moment on the intro's
 * forward schedule, from 0 (only the sigil) to the timeline's end (the
 * whole tree). Opening runs it forward; closing runs it backward, faster;
 * turning around partway just changes direction, so the tree never jumps.
 * Each animated part registers its element, and every frame the clock
 * writes each part's pose straight onto it, without React re-rendering.
 */
export class SkillTreeIntroClock {
    private readonly parts = new Map<SVGElement, Part>();
    private timeline: number;
    private time = 0;
    private direction: SkillTreeIntroDirection | null = null;
    private frame = 0;
    private lastFrameAt = 0;
    private readonly onSettle: (direction: SkillTreeIntroDirection) => void;

    constructor(timeline: number, onSettle: (direction: SkillTreeIntroDirection) => void) {
        this.timeline = timeline;
        this.onSettle = onSettle;
    }

    /** Where the clock is, in seconds on the forward schedule. */
    get position(): number {
        return this.time;
    }

    /** Updates the schedule's length (the tree changed), keeping the clock where it is. */
    setTimeline(timeline: number): void {
        this.timeline = timeline;
        this.time = Math.min(this.time, timeline);
    }

    register(element: SVGElement, window: SkillTreeIntroWindow, motion: SkillTreeIntroMotion, options: SkillTreeIntroPartOptions = {}): void {
        const part = { window, motion, options };
        this.parts.set(element, part);
        if (options.origin) element.style.transformOrigin = `${options.origin.x}px ${options.origin.y}px`;
        this.writePart(element, part);
    }

    /** Stops animating a part and clears what the clock wrote, leaving it as it would be without the intro. */
    unregister(element: SVGElement): void {
        if (!this.parts.delete(element)) return;
        writePose(element, {});
        element.style.transformOrigin = '';
    }

    /** Runs the clock forward (opening) or backward (closing) from wherever it is. */
    play(direction: SkillTreeIntroDirection): void {
        this.direction = direction;
        if (this.frame) return;
        this.lastFrameAt = performance.now();
        const tick = (now: number): void => {
            const seconds = (now - this.lastFrameAt) / 1000;
            this.lastFrameAt = now;
            // Cap a frame's step, so a backgrounded tab finishes smoothly rather than in one leap.
            const settled = this.advance(Math.min(seconds, MAX_FRAME_SECONDS));
            this.frame = settled ? 0 : requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
    }

    /** Sets the clock to a moment without playing, posing every part there (0 rewinds to just the sigil). */
    seek(position: number): void {
        this.cancelFrame();
        this.direction = null;
        this.time = Math.min(this.timeline, Math.max(0, position));
        this.writeAll();
    }

    /** Jumps to the finished tree (a skip). */
    finish(): void {
        this.direction = 'forward';
        this.time = this.timeline;
        this.cancelFrame();
        this.writeAll();
        this.onSettle('forward');
    }

    /**
     * Moves the clock by `seconds` in its direction and poses every part.
     * Returns true once it has reached the end it was heading for, after
     * telling `onSettle`.
     */
    advance(seconds: number): boolean {
        const direction = this.direction;
        if (!direction) return true;
        const scaled = seconds * SKILL_TREE_INTRO_SPEED_MOD;
        const step = direction === 'forward' ? scaled : -scaled * SKILL_TREE_OUTRO_SPEED_MOD;
        this.time = Math.min(this.timeline, Math.max(0, this.time + step));
        this.writeAll();
        const settled = direction === 'forward' ? this.time >= this.timeline : this.time <= 0;
        if (settled) {
            this.direction = null;
            this.onSettle(direction);
        }
        return settled;
    }

    dispose(): void {
        this.cancelFrame();
        for (const element of [...this.parts.keys()]) this.unregister(element);
    }

    private cancelFrame(): void {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
    }

    private writeAll(): void {
        this.parts.forEach((part, element) => this.writePart(element, part));
    }

    private writePart(element: SVGElement, part: Part): void {
        const progress = part.window.duration > 0 ? (this.time - part.window.start) / part.window.duration : this.time >= part.window.start ? 1 : 0;
        writePose(element, resolveSkillTreeIntroPose(part.motion, progress, part.options));
    }
}
